/**
 * Thin fetch wrapper that adds the JWT access token to every request and
 * transparently refreshes it on 401.
 */
import {
  clearImpersonationOverlay,
  getActiveAccessToken,
  isImpersonating,
  refreshAccessToken,
} from "@/lib/auth";
import { isSiteGateRejection, redirectToSiteGate } from "@/lib/site-gate";

const API_URL = import.meta.env.VITE_API_URL || "";
const API_PREFIX = import.meta.env.VITE_API_PREFIX || "/api/v1";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public payload: unknown,
    public requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  skipAuth?: boolean;
}

const REQUEST_ID_HEADER = "X-Request-ID";

/** 32-char hex matches the backend's `uuid4().hex` style; stays under
 *  the middleware's 64-char inbound cap. Falls back for older runtimes
 *  that lack `crypto.randomUUID`. */
function mintRequestId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID().replace(/-/g, "");
  }
  return Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 16).toString(16),
  ).join("");
}

async function buildRequest(
  path: string,
  options: ApiOptions,
  requestId: string,
): Promise<Request> {
  const { body, skipAuth, headers: rawHeaders, ...rest } = options;
  const headers = new Headers(rawHeaders);
  if (!headers.has("Content-Type") && body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has(REQUEST_ID_HEADER)) {
    headers.set(REQUEST_ID_HEADER, requestId);
  }
  if (!skipAuth) {
    const token = getActiveAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const init: RequestInit = {
    ...rest,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };
  return new Request(`${API_URL}${API_PREFIX}${path}`, init);
}

/** Prefer the server-echoed id; fall back to the one we sent so support
 *  tickets always quote a usable correlation id. */
function resolveRequestId(response: Response, sent: string): string {
  return response.headers.get(REQUEST_ID_HEADER) || sent;
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("Content-Type") || "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }
  return (await response.text()) as unknown as T;
}

/** Handle a 401 and report whether the request is worth retrying.
 *
 *  `tokenUsed` is the access token the failed request actually carried. If the
 *  stored token has moved on since, a parallel request (or another tab) has
 *  already refreshed — retry on theirs instead of burning a rotation of our
 *  own, which under BLACKLIST_AFTER_ROTATION would invalidate the fresh pair. */
async function recoverFromUnauthorized(
  tokenUsed: string | null,
): Promise<boolean> {
  if (isImpersonating()) {
    // Impersonation token expired/invalid → drop the overlay and retry on
    // the admin's own session (which is still intact).
    clearImpersonationOverlay();
    return true;
  }
  if (tokenUsed !== null && getActiveAccessToken() !== tokenUsed) return true;
  try {
    return (await refreshAccessToken()) !== null;
  } catch {
    // A failing refresh shouldn't mask the original 401 — let the caller
    // surface the ApiError below.
    return false;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const requestId = mintRequestId();
  const tokenUsed = options.skipAuth ? null : getActiveAccessToken();
  const request = await buildRequest(path, options, requestId);
  let response = await fetch(request);

  // The gate rejects before the request ever reaches Django, so there is no
  // token to refresh — checked first to avoid burning a rotation on it.
  if (isSiteGateRejection(response)) {
    redirectToSiteGate();
  } else if (response.status === 401 && !options.skipAuth) {
    if (await recoverFromUnauthorized(tokenUsed)) {
      const retry = await buildRequest(path, options, requestId);
      response = await fetch(retry);
    }
  }

  if (!response.ok) {
    const payload = await parse<unknown>(response);
    throw new ApiError(
      `Request failed (${response.status})`,
      response.status,
      payload,
      resolveRequestId(response, requestId),
    );
  }
  return parse<T>(response);
}

/** Fetch a non-JSON response (e.g. a streamed CSV download) as a Blob.
 *  Routes through the same Bearer-token + 401-refresh logic as apiFetch. */
export async function apiBlob(
  path: string,
  options: ApiOptions = {},
): Promise<Blob> {
  const requestId = mintRequestId();
  const tokenUsed = options.skipAuth ? null : getActiveAccessToken();
  const request = await buildRequest(path, options, requestId);
  let response = await fetch(request);
  if (isSiteGateRejection(response)) {
    redirectToSiteGate();
  } else if (response.status === 401 && !options.skipAuth) {
    if (await recoverFromUnauthorized(tokenUsed)) {
      const retry = await buildRequest(path, options, requestId);
      response = await fetch(retry);
    }
  }
  if (!response.ok) {
    const payload = await parse<unknown>(response);
    throw new ApiError(
      `Request failed (${response.status})`,
      response.status,
      payload,
      resolveRequestId(response, requestId),
    );
  }
  return response.blob();
}

export const api = {
  get: <T = unknown>(path: string, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "GET" }),
  post: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "POST", body }),
  put: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "PUT", body }),
  patch: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "PATCH", body }),
  delete: <T = unknown>(path: string, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "DELETE" }),
  blob: apiBlob,
};

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
